// src/components/resources/layouts/GeneratorCompanionCallout.tsx

'use client'

import Link from 'next/link'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import {
  trackGeneratorBridgeClick,
  type GeneratorBridgeTarget,
} from '~/components/resources/tools/generators/shared/trackGeneratorBridge'

/**
 * A single companion-tool cross-link surfaced below the generator intro.
 *
 * @interface GeneratorCompanionLink
 * @property {string} href - Internal route to the companion tool or resource.
 * @property {string} title - Short link label.
 * @property {string} description - One-line value prop for the destination.
 */
export interface GeneratorCompanionLink {
  href: string
  title: string
  description: string
}

/**
 * Maps an internal href to a bridge target type for funnel reporting.
 *
 * Editorial destinations (blog posts, guides) are classified as `guide` —
 * NOT the `companion_tool` fallback — so a guide companion link can't
 * pollute the tool cross-sell funnel split (r3542875315). Exported for the
 * mapping test.
 *
 * @param {string} href - Destination path.
 * @returns {GeneratorBridgeTarget} Analytics target category.
 */
export function bridgeTargetForHref(href: string): GeneratorBridgeTarget {
  if (href === '/request-access') return 'request_access'
  if (href.startsWith('/blog/')) return 'guide'
  if (href.startsWith('/resources/guides/')) return 'guide'
  if (href.startsWith('/resources/tools/generators/')) return 'companion_tool'
  if (href.startsWith('/resources/tools/analyzers/')) return 'analyzer'
  if (href.startsWith('/resources/tools/calculators/')) return 'calculator'
  return 'companion_tool'
}

/**
 * Renders a compact callout grid linking to related mockups, calculators, or analyzers.
 *
 * @param {Object} props - Component props.
 * @param {GeneratorCompanionLink[]} props.links - Companion destinations (1–3 recommended).
 * @param {string} [props.resourceSlug] - Source generator slug for bridge analytics.
 * @returns {JSX.Element | null} Callout block or null when empty.
 */
export function GeneratorCompanionCallout({
  links,
  resourceSlug,
}: {
  links: readonly GeneratorCompanionLink[]
  resourceSlug?: string
}): JSX.Element | null {
  if (links.length === 0) return null

  return (
    <aside
      className="mt-8 max-w-3xl rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5 dark:border-indigo-500/20 dark:bg-indigo-500/5"
      aria-label="Related resources"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
        Also try
      </p>
      <ul className="mt-3 space-y-3">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              onClick={() => {
                if (!resourceSlug) return
                trackGeneratorBridgeClick({
                  resourceSlug,
                  placement: 'companion',
                  targetType: bridgeTargetForHref(link.href),
                  destinationPath: link.href,
                })
              }}
              className="group flex items-start justify-between gap-3 rounded-lg p-2 -mx-2 transition-colors hover:bg-white/80 dark:hover:bg-gray-900/40"
            >
              <span>
                <span className="font-medium text-gray-900 dark:text-white group-hover:text-indigo-700 dark:group-hover:text-indigo-300">
                  {link.title}
                </span>
                <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-400">
                  {link.description}
                </span>
              </span>
              <RiArrowRightLine
                aria-hidden
                className="mt-1 h-4 w-4 flex-shrink-0 text-indigo-500 opacity-70 transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  )
}
